import { useCallback, useEffect, useMemo, useState } from 'react';
import { DeleteOutlined, FilterOutlined, PlusOutlined } from '@ant-design/icons';
import { Alert, Button, Form, Input, Modal, Radio, Select } from 'antd';
import type {
  FieldDefinition,
  FilterLeaf,
  LinkAggregateColumnConfig,
} from '@lens/shared';
import { listFields } from '../item-types/api.ts';
import { getDictionary, listEntries as listDictEntries } from '../dictionaries/api.ts';
import { entryLabel } from '../dictionaries/labelHelpers.ts';
import {
  FilterValueInput,
  OPERATOR_LABELS,
  getOperatorsForFieldType,
} from '../../shared/components/FilterRow.tsx';

/**
 * Модалка «+ Колонка из связей» (M11.6).
 *
 * UX: пользователь выбирает тип связанных объектов и, при необходимости,
 * фильтры по их полям. Колонка берёт текущие item_links записи, а не правила,
 * которыми эти связи когда-то были созданы.
 *
 * onSubmit получает готовый `LinkAggregateColumnConfig` без `key` (key
 * генерируется в SpacePage перед записью в view.config — это держит
 * генерацию идентификатора в одном месте).
 */
export interface LinkAggregateColumnModalProps {
  open: boolean;
  spaceId: string;
  itemTypeIds: string[];
  availableItemTypeIds?: string[];
  itemTypeNames: Record<string, string>;
  initialConfig?: LinkAggregateColumnConfig | null;
  onClose: () => void;
  onSubmit: (cfg: Omit<LinkAggregateColumnConfig, 'key'>) => Promise<void> | void;
}

type Mode = LinkAggregateColumnConfig['mode'];

const MODE_OPTIONS: Array<{ value: Mode; label: string }> = [
  { value: 'list', label: 'Список' },
  { value: 'count', label: 'Количество' },
  { value: 'sum', label: 'Сумма' },
  { value: 'avg', label: 'Среднее' },
];

function defaultLabel(mode: Mode, linkedTypeNames: string[], fieldName?: string): string {
  let target = '';
  if (linkedTypeNames.length === 1) target = `: ${linkedTypeNames[0]}`;
  else if (linkedTypeNames.length > 1) target = ` (${linkedTypeNames.length} типов)`;
  switch (mode) {
    case 'list':
      return `Связи${target}`;
    case 'count':
      return `Количество${target}`;
    case 'sum':
      return `Σ ${fieldName ?? 'значение'}${target}`;
    case 'avg':
      return `Среднее ${fieldName ?? 'значение'}${target}`;
  }
}

export function LinkAggregateColumnModal({
  open,
  spaceId,
  itemTypeIds,
  availableItemTypeIds,
  itemTypeNames,
  initialConfig,
  onClose,
  onSubmit,
}: LinkAggregateColumnModalProps): JSX.Element {
  const typeOptions = availableItemTypeIds ?? itemTypeIds;
  const [linkedItemTypeIds, setLinkedItemTypeIds] = useState<string[]>([]);
  const [mode, setMode] = useState<Mode>('count');
  const [valueFieldSlug, setValueFieldSlug] = useState<string | undefined>(undefined);
  const [linkedFields, setLinkedFields] = useState<FieldDefinition[]>([]);
  const [linkedFieldsLoading, setLinkedFieldsLoading] = useState(false);
  const [linkedFilters, setLinkedFilters] = useState<FilterLeaf[]>([]);
  const [refOptionsByField, setRefOptionsByField] = useState<
    Record<string, Array<{ value: string; label: string }>>
  >({});
  const [label, setLabel] = useState('');
  const [labelEdited, setLabelEdited] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset на открытие.
  useEffect(() => {
    if (!open) return;
    const initialTypes = [
      ...new Set([
        ...(initialConfig?.linkedItemTypeId ? [initialConfig.linkedItemTypeId] : []),
        ...((initialConfig?.linkedItemTypeIds ?? []) as string[]),
      ]),
    ];
    setLinkedItemTypeIds(initialTypes);
    setMode(initialConfig?.mode ?? 'count');
    setValueFieldSlug(initialConfig?.valueFieldSlug);
    setLinkedFields([]);
    setLinkedFilters(initialConfig?.linkedFilters ?? []);
    setRefOptionsByField({});
    setLabel(initialConfig?.label ?? '');
    setLabelEdited(false);
    setError(null);
  }, [open, initialConfig]);

  // Поля связанных типов нужны для sum/avg и фильтров. Если выбрано несколько
  // типов, оставляем поля с общим slug, чтобы фильтр имел одинаковую семантику.
  useEffect(() => {
    if (!open || linkedItemTypeIds.length === 0) {
      setLinkedFields([]);
      return;
    }
    let cancelled = false;
    setLinkedFieldsLoading(true);
    Promise.all(linkedItemTypeIds.map((tid) => listFields(spaceId, tid)))
      .then((fieldsByType) => {
        if (cancelled) return;
        const slugCounts = new Map<string, { field: FieldDefinition; count: number }>();
        for (const list of fieldsByType) {
          const seen = new Set<string>();
          for (const f of list) {
            if (seen.has(f.slug)) continue;
            seen.add(f.slug);
            const prev = slugCounts.get(f.slug);
            if (prev) prev.count += 1;
            else slugCounts.set(f.slug, { field: f, count: 1 });
          }
        }
        const intersection: FieldDefinition[] = [];
        for (const { field, count } of slugCounts.values()) {
          if (count === fieldsByType.length) intersection.push(field);
        }
        setLinkedFields(intersection);
      })
      .catch(() => {
        if (cancelled) return;
        setLinkedFields([]);
      })
      .finally(() => {
        if (!cancelled) setLinkedFieldsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, spaceId, linkedItemTypeIds]);

  // Загрузка опций справочника для reference-полей (аналогично таблице).
  const ensureRefForField = useCallback(
    async (field: FieldDefinition): Promise<void> => {
      if (refOptionsByField[field.slug]) return;
      const config = field.config as { dictionaryId?: string } | null;
      const dictionaryId = config?.dictionaryId;
      if (!dictionaryId) return;
      try {
        const [dict, result] = await Promise.all([
          getDictionary(dictionaryId),
          listDictEntries(dictionaryId, { limit: 200 }),
        ]);
        const opts = result.items.map((entry) => ({
          value: entry.id,
          label: entryLabel(entry, dict),
        }));
        setRefOptionsByField((prev) => ({ ...prev, [field.slug]: opts }));
      } catch {
        // не шумим
      }
    },
    [refOptionsByField],
  );

  // Авто-предзаполнение label.
  const linkedTypeNames = useMemo(
    () => linkedItemTypeIds.map((id) => itemTypeNames[id] ?? id),
    [linkedItemTypeIds, itemTypeNames],
  );
  const fieldName = useMemo(
    () => linkedFields.find((f) => f.slug === valueFieldSlug)?.name,
    [linkedFields, valueFieldSlug],
  );
  useEffect(() => {
    if (labelEdited) return;
    setLabel(
      initialConfig?.label && !labelEdited
        ? initialConfig.label
        : defaultLabel(mode, linkedTypeNames, fieldName),
    );
  }, [initialConfig, labelEdited, mode, linkedTypeNames, fieldName]);

  const sumAvgRequiresField = (mode === 'sum' || mode === 'avg') && !valueFieldSlug;
  const submitDisabled = !label.trim() || sumAvgRequiresField || submitting;

  const handleOk = async () => {
    if (submitDisabled) return;
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({
        kind: 'link_aggregate',
        label: label.trim(),
        linkedItemTypeIds: linkedItemTypeIds.length > 0 ? linkedItemTypeIds : undefined,
        linkedFilters: linkedFilters.length > 0 ? linkedFilters : undefined,
        mode,
        valueFieldSlug: mode === 'sum' || mode === 'avg' ? valueFieldSlug : undefined,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось сохранить колонку');
    } finally {
      setSubmitting(false);
    }
  };

  const addFilter = (): void => {
    const first = linkedFields[0];
    if (!first) return;
    const operators = getOperatorsForFieldType(first.fieldType);
    setLinkedFilters((prev) => [
      ...prev,
      {
        fieldSlug: first.slug,
        operator: operators[0] ?? 'eq',
        value: undefined,
        ...(prev.length > 0 ? { combinator: 'and' as const } : {}),
      },
    ]);
    if (first.fieldType === 'reference') void ensureRefForField(first);
  };

  const updateFilter = (idx: number, patch: Partial<FilterLeaf>): void => {
    setLinkedFilters((prev) =>
      prev.map((filter, i) => (i === idx ? { ...filter, ...patch } : filter)),
    );
  };

  const numericFields = linkedFields.filter(
    (f) => f.fieldType === 'number' || f.fieldType === 'computed',
  );

  return (
    <Modal
      open={open}
      title="Колонка из связей"
      okText={initialConfig ? 'Сохранить' : 'Добавить'}
      cancelText="Отмена"
      onCancel={onClose}
      onOk={handleOk}
      okButtonProps={{ disabled: submitDisabled, loading: submitting }}
      destroyOnHidden
    >
      {error ? (
        <Alert type="error" message={error} style={{ marginBottom: 12 }} />
      ) : null}
      <Form layout="vertical">
        <Form.Item
          label="Тип связанной записи"
          extra="Если тип не выбран, колонка учитывает все текущие связи записи."
        >
          <Select
            aria-label="Тип связанной записи"
            mode="multiple"
            allowClear
            placeholder="Любой тип"
            value={linkedItemTypeIds}
            onChange={(v) => {
              setLinkedItemTypeIds(v as string[]);
              setValueFieldSlug(undefined);
              setLinkedFilters([]);
              setRefOptionsByField({});
            }}
            options={typeOptions.map((id) => ({ value: id, label: itemTypeNames[id] ?? id }))}
            maxTagCount="responsive"
          />
        </Form.Item>
        <Form.Item label="Режим">
          <Radio.Group
            options={MODE_OPTIONS}
            value={mode}
            onChange={(e) => {
              const next = e.target.value as Mode;
              setMode(next);
              if (next !== 'sum' && next !== 'avg') setValueFieldSlug(undefined);
            }}
            optionType="button"
            buttonStyle="solid"
          />
        </Form.Item>
        {mode === 'sum' || mode === 'avg' ? (
          <Form.Item
            label="Числовое поле связанного объекта"
            required
            extra={
              linkedItemTypeIds.length > 1
                ? 'Для нескольких типов доступны только общие поля по slug'
                : undefined
            }
          >
            <Select
              placeholder={
                linkedItemTypeIds.length === 0
                  ? 'Сначала выберите тип связанной записи'
                  : numericFields.length === 0
                    ? 'Нет общих числовых полей'
                    : 'Выберите поле'
              }
              value={valueFieldSlug}
              onChange={(v) => setValueFieldSlug(v)}
              options={numericFields.map((f) => ({ value: f.slug, label: f.name }))}
              disabled={linkedItemTypeIds.length === 0 || linkedFieldsLoading}
            />
          </Form.Item>
        ) : null}
        <Form.Item label="Фильтры по связанным объектам">
          <div className="lens-filter-editor" style={{ width: '100%' }}>
            {linkedFilters.length > 0 && (
              <div className="lens-filter-editor__head">
                <span className="lens-filter-editor__title">
                  <FilterOutlined style={{ fontSize: 12 }} />
                  Фильтр
                  <span className="lens-filter-editor__count">{linkedFilters.length}</span>
                </span>
                <div className="lens-filter-editor__head-actions">
                  <Button
                    size="small"
                    type="text"
                    danger
                    onClick={() => setLinkedFilters([])}
                    className="lens-filter-editor__clear"
                  >
                    Очистить всё
                  </Button>
                </div>
              </div>
            )}
            {linkedFilters.map((filter, index) => {
              const field = linkedFields.find((f) => f.slug === filter.fieldSlug) ?? null;
              const operators = field ? getOperatorsForFieldType(field.fieldType) : ['eq', 'neq'];
              const noValue = ['is_empty', 'is_not_empty'].includes(filter.operator);
              return (
                <div key={index} className="lens-filter-editor__row">
                  {index === 0 ? (
                    <span className="lens-filter-editor__lead">где</span>
                  ) : (
                    <Select
                      size="small"
                      style={{ width: 72, flexShrink: 0 }}
                      className="lens-filter-editor__combinator"
                      value={filter.combinator === 'or' ? 'or' : 'and'}
                      onChange={(v) => updateFilter(index, { combinator: v as 'and' | 'or' })}
                      options={[
                        { value: 'and', label: 'И' },
                        { value: 'or', label: 'ИЛИ' },
                      ]}
                    />
                  )}
                  <Select
                    size="small"
                    style={{ flex: '0 1 220px', minWidth: 160 }}
                    showSearch
                    filterOption={(input, opt) =>
                      (opt?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                    }
                    value={filter.fieldSlug}
                    onChange={(slug) => {
                      const newField = linkedFields.find((x) => x.slug === slug);
                      const ops = newField ? getOperatorsForFieldType(newField.fieldType) : ['eq'];
                      updateFilter(index, {
                        fieldSlug: slug,
                        operator: ops[0] ?? 'eq',
                        value: undefined,
                      });
                      if (newField?.fieldType === 'reference') void ensureRefForField(newField);
                    }}
                    options={linkedFields.map((x) => ({ value: x.slug, label: x.name }))}
                    placeholder="Поле"
                  />
                  <Select
                    size="small"
                    style={{ flex: '0 1 140px', minWidth: 120 }}
                    value={filter.operator}
                    onChange={(op) =>
                      updateFilter(index, {
                        operator: op,
                        value: ['is_empty', 'is_not_empty'].includes(op)
                          ? undefined
                          : filter.value,
                      })
                    }
                    options={operators.map((op) => ({ value: op, label: OPERATOR_LABELS[op] ?? op }))}
                  />
                  {!noValue && (
                    <FilterValueInput
                      field={field}
                      operator={filter.operator}
                      value={filter.value}
                      onChange={(v) => updateFilter(index, { value: v })}
                      refOptions={field ? (refOptionsByField[field.slug] ?? []) : []}
                      onRefFocus={() => {
                        if (field?.fieldType === 'reference') void ensureRefForField(field);
                      }}
                    />
                  )}
                  <Button
                    size="small"
                    type="text"
                    icon={<DeleteOutlined />}
                    onClick={() =>
                      setLinkedFilters((prev) => prev.filter((_, i) => i !== index))
                    }
                    aria-label="Удалить условие"
                    className="lens-filter-editor__remove"
                  />
                </div>
              );
            })}
            <div>
              <Button
                size="small"
                type="dashed"
                icon={<PlusOutlined />}
                onClick={addFilter}
                disabled={linkedFields.length === 0 || linkedFieldsLoading}
                className="lens-filter-editor__add"
              >
                Добавить условие
              </Button>
            </div>
          </div>
        </Form.Item>
        <Form.Item label="Заголовок колонки" required>
          <Input
            value={label}
            onChange={(e) => {
              setLabel(e.target.value);
              setLabelEdited(true);
            }}
            placeholder="Например, «Целей»"
            maxLength={120}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
